import { Link, useNavigate, useParams, useRouter } from "@tanstack/react-router";
import { CheckVerified02, Eye, HelpCircle, LogOut01, Pin02, Route, Settings04, Toggle01Right } from "@untitledui/icons";
import posthog from "posthog-js";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useMarkNavBadgeViewed, useNavBadges } from "@/api/nav-badges";
import { useTikTokShopConnectionStatus } from "@/api/tiktok-shop";
import { SWITCHER_COPY } from "@/components/admin/impersonate/constants";
import { ImpersonateModal } from "@/components/admin/impersonate-modal";
import { Button as PrimaryButton } from "@/components/base/buttons/button";
import { FeatureFlagsModal } from "@/components/feature-flags/feature-flags-modal";
import { AccountMenuHelpCenter } from "@/components/sidebar/account-menu-help-center";
import { ImpersonatedSessionPanel } from "@/components/sidebar/impersonated-session-panel";
import { MerchantSelector } from "@/components/sidebar/merchant-selector";
import { TikTokSellerConnectionsModal } from "@/components/tiktok-seller-connections-modal";
import { Button } from "@/components/ui/button";
import { NotificationDot } from "@/components/ui/notification-dot";
import { useLocalStorage } from "@/hooks/use-local-storage";
import { useSignOut } from "@/hooks/use-sign-out";
import { useSidebarStore } from "@/hooks/useSidebarStore";
import { stopImpersonation } from "@/lib/impersonation";
import { cn } from "@/lib/utils";
import { useAuth } from "@/providers/auth-provider";
import { useOnboarding } from "@/providers/onboarding-provider";
import { RESOLVES_FLAGS_LOCALLY } from "@/utils/feature-flag-source";
import { getUserInitials } from "@/utils/user-initials";

// ============================================================================
// TYPES
// ============================================================================

export interface SubNavItem {
  id: string;
  enabled?: boolean;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  badge?: boolean;
}

/** A labeled section grouping several sub-items under a header (e.g. "FIND", "MESSAGE"). */
export interface SubNavGroup {
  label?: string;
  items: SubNavItem[];
}

export interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description?: string;
  path?: string;
  // TODO: remove openInNewTab, unused after alpha release
  openInNewTab?: boolean;
  disabled?: boolean; //Disables the click action so it only works on hover
  enabled: boolean; //Removes it from the sidebar completely when false
  subItems?: (SubNavItem | SubNavGroup)[];
  badge?: boolean;
}

const flattenSubItems = (subItems: NavItem["subItems"]): SubNavItem[] =>
  (subItems ?? []).flatMap((entry) => ("items" in entry ? entry.items : [entry]));

const MAX_VISIBLE_BADGES = 3;

const isSubItemVisible = (item: SubNavItem) => item.enabled === undefined || item.enabled === true;

const isSectionVisible = (section: NavItem) => {
  if (!section.enabled) return false;
  const flatSubItems = flattenSubItems(section.subItems);
  return flatSubItems.length === 0 || flatSubItems.some(isSubItemVisible);
};

const visibleNavItemIds = (sections: NavItem[]): Set<string> => {
  const ids = new Set<string>();
  for (const section of sections) {
    if (!isSectionVisible(section)) continue;
    const flatSubItems = flattenSubItems(section.subItems);
    if (flatSubItems.length === 0) {
      ids.add(section.id);
      continue;
    }
    for (const item of flatSubItems) {
      if (isSubItemVisible(item)) ids.add(item.id);
    }
  }
  return ids;
};

export const applyNavBadges = (sections: NavItem[], badgedNavItemIds: string[]): NavItem[] => {
  const visibleIds = visibleNavItemIds(sections);
  const granted = new Set(badgedNavItemIds.filter((id) => visibleIds.has(id)).slice(0, MAX_VISIBLE_BADGES));
  if (granted.size === 0) return sections;

  return sections.map((section) => ({
    ...section,
    badge: granted.has(section.id),
    subItems: section.subItems?.map((entry) =>
      "items" in entry
        ? { ...entry, items: entry.items.map((item) => ({ ...item, badge: granted.has(item.id) })) }
        : { ...entry, badge: granted.has(entry.id) },
    ),
  }));
};

const hasRolledUpBadge = (section: NavItem) =>
  section.badge === true ||
  flattenSubItems(section.subItems).some((item) => item.badge === true && isSubItemVisible(item));

interface SidebarContextType {
  activeTab: string;
  expandedSection: string | null;
  setExpandedSection: (section: string | null) => void;
  isPinned: boolean;
  togglePin: () => void;
  handleSectionHover: (sectionId: string) => void;
}

// ============================================================================
// CONTEXT
// ============================================================================

const SidebarContext = createContext<SidebarContextType | null>(null);

export const useSidebarContext = () => {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error("useSidebarContext must be used within AppSidebar");
  return ctx;
};

// ============================================================================
// HELPERS
// ============================================================================

const formatUserName = (
  user: { firstName: string; lastName: string; email: string } | undefined,
): string | undefined => {
  if (!user) return undefined;
  return [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email;
};

// ============================================================================
// SIDEBAR FOOTER (always rendered)
// ============================================================================

interface SidebarFooterProps {
  isPinned: boolean;
  setExpandedSection: (section: string | null) => void;
}

function SidebarFooter({ isPinned, setExpandedSection }: SidebarFooterProps) {
  const navigate = useNavigate();
  const routeParams = useParams({ strict: false }) as { merchantId?: string };
  const { user } = useAuth();
  const signOut = useSignOut();
  const { openOnboarding } = useOnboarding();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isTikTokModalOpen, setIsTikTokModalOpen] = useState(false);
  const [isImpersonateModalOpen, setIsImpersonateModalOpen] = useState(false);
  const [isFeatureFlagsModalOpen, setIsFeatureFlagsModalOpen] = useState(false);
  const { data: shopStatus } = useTikTokShopConnectionStatus();

  const name = formatUserName(user);
  const initials = getUserInitials(name);
  const isImpersonating = !!user?.impersonatedBy;

  // Auto-open only when the integration actually needs re-authentication (UNB-1955) —
  // no longer a one-time "first login" popup gated by sessionStorage.
  useEffect(() => {
    if (shopStatus && !shopStatus.connected) {
      setIsTikTokModalOpen(true);
    }
  }, [shopStatus]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isUserMenuOpen && !target.closest("[data-user-menu]")) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isUserMenuOpen]);

  const handleSignOut = async () => {
    await signOut();
    navigate({ to: "/login" });
  };

  const handleSupport = () => {
    window.location.href = "mailto:support@unbox-commerce.com";
  };

  const handleProductTour = () => {
    openOnboarding();
    setIsUserMenuOpen(false);
  };

  const handleImpersonate = () => {
    setIsImpersonateModalOpen(true);
    setIsUserMenuOpen(false);
  };

  const handleFeatureFlags = () => {
    setIsFeatureFlagsModalOpen(true);
    setIsUserMenuOpen(false);
  };

  const handleProductUpdates = () => {
    const merchantId = routeParams.merchantId;
    if (merchantId) {
      navigate({ to: "/merchant/$merchantId/product-updates", params: { merchantId } });
    }
    setIsUserMenuOpen(false);
  };

  return (
    <div className="flex flex-col items-center gap-4 px-3 pb-6">
      <div className="w-10 h-10 bg-white rounded-sm overflow-visible relative z-[100]">
        <MerchantSelector
          onOpen={() => {
            if (!isPinned) setExpandedSection(null);
          }}
        />
      </div>

      <div className="relative" data-user-menu>
        <button
          type="button"
          onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
          title={isImpersonating ? SWITCHER_COPY.active(name || "User") : undefined}
          className={cn(
            "relative w-10 h-10 rounded-full border flex items-center justify-center transition-colors",
            isImpersonating
              ? "bg-brand-solid border-brand hover:bg-brand-solid_hover"
              : "bg-gray-100 border-gray-200 hover:bg-gray-200",
          )}
        >
          <span className={cn("text-base font-semibold", isImpersonating ? "text-white" : "text-gray-500")}>
            {initials}
          </span>
          {isImpersonating && (
            <span
              aria-hidden="true"
              className="absolute -right-0.5 -bottom-0.5 flex size-3.5 items-center justify-center rounded-full bg-unbox-pink ring-2 ring-white"
            >
              <Eye className="size-2.5 text-gray-900" />
            </span>
          )}
        </button>

        {isUserMenuOpen && (
          <div className="absolute bottom-0 left-full ml-2 w-[265px] bg-white border border-gray-200 rounded-lg shadow-none z-[100] overflow-hidden">
            <div className="py-2">
              <button
                type="button"
                onClick={handleSupport}
                className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50 transition-colors"
              >
                <HelpCircle className="w-5 h-5 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">Support</span>
              </button>

              <button
                type="button"
                onClick={handleProductTour}
                className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50 transition-colors"
              >
                <Route className="w-5 h-5 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">Product Tour</span>
              </button>

              <button
                type="button"
                onClick={handleProductUpdates}
                className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50 transition-colors"
              >
                <CheckVerified02 className="w-5 h-5 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">Product Updates</span>
              </button>

              <AccountMenuHelpCenter />

              {isImpersonating && user ? (
                <ImpersonatedSessionPanel user={user} name={name || "User"} onEnd={stopImpersonation} />
              ) : (
                <>
                  <div className="border-t border-gray-100 my-2" />
                  <div className="px-4 py-3 flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-full border border-gray-200 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-gray-500">{initials}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{name || "User"}</div>
                      <div className="text-xs text-gray-500 truncate">{user?.email || "user@example.com"}</div>
                    </div>
                  </div>
                  {user?.role === "admin" && (
                    <div className="flex flex-col gap-2 px-4 pt-1 pb-4">
                      <PrimaryButton
                        color="primary"
                        size="sm"
                        iconLeading={Settings04}
                        className="w-full"
                        onClick={handleImpersonate}
                      >
                        {SWITCHER_COPY.open}
                      </PrimaryButton>
                      {RESOLVES_FLAGS_LOCALLY && (
                        <PrimaryButton
                          color="secondary"
                          size="sm"
                          iconLeading={Toggle01Right}
                          className="w-full"
                          onClick={handleFeatureFlags}
                        >
                          Configure flags
                        </PrimaryButton>
                      )}
                    </div>
                  )}
                </>
              )}

              <div className="border-t border-gray-100 my-2" />

              <button
                type="button"
                onClick={handleSignOut}
                className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50 transition-colors"
              >
                <LogOut01 className="w-5 h-5 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">Log out</span>
              </button>
            </div>
          </div>
        )}
      </div>

      <TikTokSellerConnectionsModal open={isTikTokModalOpen} onOpenChange={setIsTikTokModalOpen} />
      <ImpersonateModal open={isImpersonateModalOpen} onOpenChange={setIsImpersonateModalOpen} />
      <FeatureFlagsModal open={isFeatureFlagsModalOpen} onOpenChange={setIsFeatureFlagsModalOpen} />
    </div>
  );
}

// ============================================================================
// SIDEBAR NAVIGATION SECTION (reusable for different navigation configs)
// ============================================================================

interface SidebarNavigationProps {
  sections: NavItem[];
}

function SidebarNavigationIcons({ sections }: SidebarNavigationProps) {
  const navigate = useNavigate();
  const router = useRouter();
  const routeParams = useParams({ strict: false });
  const { activeTab, expandedSection, setExpandedSection, handleSectionHover, isPinned, togglePin } =
    useSidebarContext();

  const getActiveSection = () => {
    return sections.find(
      (section) => section.id === activeTab || flattenSubItems(section.subItems).some((item) => item.id === activeTab),
    );
  };

  const activeSection = getActiveSection();

  return (
    /** biome-ignore lint/a11y/noStaticElementInteractions: hover-only interaction for navigation */
    <div
      className="flex flex-col gap-8"
      onMouseEnter={() => {
        if (activeSection?.id) {
          handleSectionHover(activeSection.id);
        }
      }}
    >
      <div className="flex items-center justify-center px-3 pt-6">
        <img className="w-[32px] h-[24px]" src="/logomark.svg" alt="Unbox Logo Mark" />
      </div>

      <div className="flex flex-col items-center px-3 gap-1.5">
        {sections.map((section) => {
          if (!section.enabled) return null;

          const flatSubItems = flattenSubItems(section.subItems);
          // Every subItem can end up disabled for this user at once (e.g. a role/feature check
          // that gates them all identically) — nothing to navigate to, so hide the section
          // entirely instead of leaving a dead icon around.
          const hasNoEnabledSubItems =
            flatSubItems.length > 0 &&
            !flatSubItems.some((item) => item.enabled === undefined || item.enabled === true);
          if (hasNoEnabledSubItems) return null;

          const isActive =
            activeSection?.id === section.id ||
            (expandedSection === section.id &&
              flattenSubItems(activeSection?.subItems).some((item) => item.id === activeTab));
          const Icon = section.icon;

          const handleSectionClick = () => {
            if (section.disabled) return;
            if (flatSubItems.length > 0) {
              const firstSubItem = flatSubItems.filter(
                (item) => item.enabled === undefined || item.enabled === true,
              )[0];
              if (!firstSubItem) return;
              setExpandedSection(section.id);
              if (section.openInNewTab) {
                const href = router.buildLocation({ to: firstSubItem.path, params: routeParams }).href;
                window.open(href, "_blank", "noopener,noreferrer");
              } else {
                navigate({ to: firstSubItem.path, params: routeParams });
              }
            } else if (section.path) {
              if (isPinned) {
                togglePin();
              }
              if (section.openInNewTab) {
                const href = router.buildLocation({ to: section.path, params: routeParams }).href;
                window.open(href, "_blank", "noopener,noreferrer");
              } else {
                navigate({ to: section.path, params: routeParams });
              }
            }
          };

          return (
            <button
              type="button"
              key={section.id}
              onMouseEnter={() => handleSectionHover(section.id)}
              onClick={handleSectionClick}
              className={cn(
                "w-14 h-14 p-2 rounded-md flex flex-col items-center justify-center gap-1 transition-colors",
                isActive
                  ? "bg-unbox-cream-50 text-gray-900"
                  : "text-gray-400 hover:bg-unbox-cream-50 hover:text-gray-700",
              )}
            >
              <span className="relative">
                <Icon className="w-6 h-6" />
                {hasRolledUpBadge(section) && <NotificationDot className="absolute -top-0.5 -right-0.5" />}
              </span>
              <span className="text-[10px] font-semibold leading-[10px]">{section.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// SIDEBAR EXPANDED CONTENT
// ============================================================================

function SidebarExpandedContent({ sections }: SidebarNavigationProps) {
  const { activeTab, expandedSection, isPinned, togglePin } = useSidebarContext();
  const routeParams = useParams({ strict: false });

  const currentSection = sections.find((s) => s.id === expandedSection);

  return (
    <div
      className={cn(
        "px-4 pt-6 pb-4 h-full flex flex-col gap-8 opacity-0 delay-100 transition-opacity",
        expandedSection && "opacity-100",
      )}
    >
      <Button
        variant="ghost"
        size="icon"
        onClick={togglePin}
        className={cn(
          "absolute top-5 right-4 w-4 h-4 p-0 transition-colors",
          isPinned ? "hover:opacity-80" : "text-gray-400 hover:text-gray-600",
        )}
        style={isPinned ? { color: "#6941C6" } : {}}
      >
        <Pin02 className="w-[14.29px] h-[14.29px]" />
      </Button>

      <div className="flex flex-col gap-1">
        <h2 className="text-base font-semibold leading-6" style={{ color: "#6941C6" }}>
          {currentSection?.label}
        </h2>
        <p className="text-sm font-normal leading-5" style={{ color: "#717680" }}>
          {currentSection?.description}
        </p>
      </div>

      <div className="flex flex-col">
        {currentSection?.subItems?.map((entry) => {
          const renderItem = (item: SubNavItem) => {
            if (item?.enabled !== undefined && !item.enabled) return null;
            const Icon = item.icon;
            return (
              <div key={item.id} className="py-0.5">
                <Link
                  to={item.path}
                  params={routeParams}
                  preload={false}
                  target={currentSection?.openInNewTab ? "_blank" : undefined}
                  rel={currentSection?.openInNewTab ? "noopener noreferrer" : undefined}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 rounded-md text-base font-semibold leading-6 transition-colors w-full",
                    activeTab === item.id ? "bg-unbox-cream-50" : "bg-white hover:bg-unbox-cream-50",
                  )}
                  style={{ color: "#414651" }}
                >
                  <Icon className="w-5 h-5 text-gray-400" />
                  {item.label}
                  {item.badge && <NotificationDot className="ml-auto" />}
                </Link>
              </div>
            );
          };

          if ("items" in entry) {
            if (!entry.items.some(isSubItemVisible)) return null;
            const groupKey = entry.label ?? entry.items.map((item) => item.id).join("-");
            return (
              <div key={groupKey} className="mb-3">
                {entry.label && (
                  <p className="text-xs font-semibold uppercase tracking-wide mt-3 mb-3" style={{ color: "#717680" }}>
                    {entry.label}
                  </p>
                )}
                {entry.items.map(renderItem)}
              </div>
            );
          }

          return renderItem(entry);
        })}
      </div>
    </div>
  );
}

// ============================================================================
// APP SIDEBAR (shell component)
// ============================================================================

interface SidebarLayoutProps {
  activeTab: string;
  sections: NavItem[];
  hasAdminBar?: boolean;
}

export function SidebarLayout({ activeTab, sections, hasAdminBar }: SidebarLayoutProps) {
  const { user } = useAuth();
  const [pinnedSection, setPinnedSection] = useLocalStorage<string | null>("sidebar-pin", null);
  const expandedSection = useSidebarStore((state) => state.expandedSection);
  const setExpandedSection = useSidebarStore((state) => state.setExpandedSection);
  const { data: navBadgeData } = useNavBadges();
  const { mutate: markNavBadgeViewed } = useMarkNavBadgeViewed();
  const markedNavItemIds = useRef<Set<string>>(new Set());

  const badgedSections = applyNavBadges(sections, navBadgeData?.badges.map((badge) => badge.navItemId) ?? []);

  const isPinned = !!pinnedSection;
  const isExpanded = !!expandedSection;

  useEffect(() => {
    if (!activeTab || markedNavItemIds.current.has(activeTab)) return;
    if (!navBadgeData?.badges.some((badge) => badge.navItemId === activeTab)) return;
    markedNavItemIds.current.add(activeTab);
    markNavBadgeViewed(activeTab);
  }, [activeTab, navBadgeData, markNavBadgeViewed]);

  // When sections change and pinned section is invalid, update to first enabled section
  useEffect(() => {
    if (pinnedSection && !sections.some((s) => s.id === pinnedSection && s.enabled)) {
      const firstEnabled = sections.find((s) => s.enabled);
      const newSection = firstEnabled?.id ?? null;
      setPinnedSection(newSection);
      setExpandedSection(newSection);
    }
  }, [pinnedSection, sections, setPinnedSection, setExpandedSection]);

  useEffect(() => {
    if (pinnedSection && !expandedSection) {
      setExpandedSection(pinnedSection);
    }
  }, [pinnedSection, expandedSection, setExpandedSection]);

  useEffect(() => {
    if (!user?.email) return;
    // Identifying here would move the admin's browsing onto the impersonated
    // user's profile; the distinct id from before the session is kept and only
    // flag evaluation is pointed at the impersonated user's email.
    if (user.impersonatedBy) {
      posthog.setPersonPropertiesForFlags({ email: user.email });
      return;
    }
    posthog.identify(user.id, {
      name: formatUserName(user),
      merchants: user.merchants,
      email: user.email,
      userId: user.id,
    });
  }, [user]);

  const handleSectionHover = (sectionId: string) => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section || flattenSubItems(section.subItems).length === 0) {
      if (!isPinned) {
        setExpandedSection(null);
      }
      return;
    }
    setExpandedSection(sectionId);
    if (isPinned) {
      setPinnedSection(sectionId);
    }
  };

  const handleSidebarLeave = () => {
    if (!isPinned) {
      setExpandedSection(null);
      return;
    }
    const activeSection = sections.find(
      (section) => section.id === activeTab || flattenSubItems(section.subItems).some((item) => item.id === activeTab),
    );
    if (activeSection && flattenSubItems(activeSection.subItems).length > 0) {
      setExpandedSection(activeSection.id);
    } else {
      togglePin();
    }
  };

  const togglePin = () => {
    if (isPinned) {
      setExpandedSection(null);
      setPinnedSection(null);
    } else {
      setPinnedSection(expandedSection);
    }
  };

  const contextValue: SidebarContextType = {
    activeTab,
    expandedSection,
    setExpandedSection,
    isPinned,
    togglePin,
    handleSectionHover,
  };

  return (
    <SidebarContext.Provider value={contextValue}>
      {user?.impersonatedBy && (
        <div
          aria-hidden="true"
          data-testid="impersonation-bar"
          className="fixed inset-x-0 top-0 z-[60] h-1 bg-brand-solid"
        />
      )}
      <nav
        className={cn(
          "flex h-full bg-white transition-all duration-300 z-[50] ease-in-out relative",
          isPinned && isExpanded ? "w-[336px]" : "w-[68px]",
        )}
        onMouseLeave={handleSidebarLeave}
      >
        {/* Main Collapsed Sidebar */}
        <div
          className={cn(
            "w-[68px] flex flex-col bg-white z-[50] fixed h-full justify-between",
            !isExpanded && "border-r border-gray-200",
            hasAdminBar && "pb-12",
          )}
          style={{ top: "var(--sidebar-top-offset, 0)" }}
        >
          <div className="flex flex-col gap-8">
            <SidebarNavigationIcons sections={badgedSections} />
          </div>
          <SidebarFooter isPinned={isPinned} setExpandedSection={setExpandedSection} />
        </div>

        {/* Expanded Sidebar Panel */}
        <div
          className={cn(
            "bg-white transition-all border-gray-200 fixed left-[68px] bottom-0 w-0",
            isExpanded && "w-[268px] border-r",
          )}
          style={{ top: "var(--sidebar-top-offset, 0)" }}
        >
          <SidebarExpandedContent sections={badgedSections} />
        </div>
      </nav>
    </SidebarContext.Provider>
  );
}
